import axios from 'axios'
import logUtil from './log-util'
import StorageUtil, {
  INTEGRATION_SELECTION_MAP_KEY,
  LOCAL,
} from './storage-util'

const API_BASE_URL = '/dashboard/api'
/*eslint-enable no-unused-vars */
const CACHE_KEY = 'integrationDetails'
const CACHE_TTL = 24 * 60 * 60 * 1000

/**
 * @typedef {Object} AccountDetail
 * @property {string} account_type
 * @property {string} account_number
 */

/**
 * @typedef {Object} Payload
 * @property {string} request_url
 * @property {string} request_payload
 */

/**
 * @typedef {Object} Operation
 * @property {string} operation_name
 * @property {string} count
 * @property {string} debug_ids
 * @property {string} payloads
 */

/**
 * @typedef {Object} PaymentIntent
 * @property {string} intent_type
 * @property {Operation[]} [operations]
 */

/**
 * @typedef {Object} IntegrationDetail
 * @property {string} app_name
 * @property {string} current_integration
 * @property {string} recommended_integration
 * @property {string} smart_payment_buttons
 * @property {string} billing_agreements
 * @property {PaymentIntent[]} payment_intents
 */

/**
 * @typedef {Object} IntegrationDetailsResponse
 * @property {AccountDetail} account_details
 * @property {IntegrationDetail[]} integration_details
 */

const localStorageUtil = new StorageUtil(LOCAL)

/**
 * @returns {Promise<IntegrationDetailsResponse>}
 */

export const getIntegrationDetails = async (accountNumber) => {
  try {
    const cachedData = JSON.parse(localStorageUtil.getItem(CACHE_KEY) || '{}')
    const currentTime = new Date().getTime()

    if (cachedData && currentTime - cachedData.timestamp < CACHE_TTL) {
      //check account_number here. if not matched, remove storage and continue.
      const data = cachedData.data
      if (data?.account_details?.account_number === accountNumber) {
        return data
      }
      logUtil.error(
        `Account number in local storage does not match with account details in the Application Context. Fetching details from API`,
      )
      localStorageUtil.removeItem(CACHE_KEY)
    }

    const urlParams = new URLSearchParams(window.location.search)
    const returnMock = urlParams.get('returnMock') === 'true'

    const apiUrl = `${API_BASE_URL}/upgradehub/integration-details${
      returnMock ? '?returnMock=true' : ''
    }`
    const response = await axios.get(apiUrl)

    const storeDataToCache = { data: response.data, timestamp: currentTime }
    localStorageUtil.setItem(CACHE_KEY, JSON.stringify(storeDataToCache))

    return response.data
  } catch (error) {
    localStorageUtil.removeItem(CACHE_KEY)
    throw new Error(error.message || 'Failed to fetch integration details')
  }
}

const INITIAL_INTEGRATION_SELECTION_MAP = {}

function getIntegrationSelectionMap() {
  try {
    const integrationSelectionMapAsString = localStorageUtil.getItem(
      INTEGRATION_SELECTION_MAP_KEY,
    )
    const integrationSelectionMap = JSON.parse(integrationSelectionMapAsString)
    return integrationSelectionMap ?? INITIAL_INTEGRATION_SELECTION_MAP
  } catch {
    return INITIAL_INTEGRATION_SELECTION_MAP
  }
}

export function setIntegrationSelectionMap(integrationSelectionMap) {
  const integrationSelectionMapAsString = JSON.stringify(
    integrationSelectionMap,
  )
  localStorageUtil.setItem(
    INTEGRATION_SELECTION_MAP_KEY,
    integrationSelectionMapAsString,
  )
}

export function setIntegrationForApplicationId(applicationId, integrationId) {
  const integrationSelectionMap = getIntegrationSelectionMap()
  integrationSelectionMap[applicationId] = integrationId

  setIntegrationSelectionMap(integrationSelectionMap)
}

export function getIntegrationForApplicationId(applicationId) {
  const integrationSelectionMap = getIntegrationSelectionMap()
  if (!integrationSelectionMap) {
    return null
  }

  const integrationId = integrationSelectionMap[applicationId]
  if (!integrationId) {
    return null
  }

  return integrationId
}
